import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, View, Text, ScrollView, RefreshControl, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PaymentSummaryProvider } from '@/features/payments/context/PaymentSummaryContext';
import { TransactionDetailsModal } from '@/features/payments/components/mobile/TransactionDetailsModal';
import { MobileAutopayDetailsModal } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectLeaseIds, selectConnectionLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetLeaseQuery } from '@/store/api';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { usePaymentMethodsList } from '@/features/payments/hooks/usePaymentMethodsList';
import { useLeaseHistory } from '@/features/payments/hooks/useLeaseHistory';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { PaymentsScreenSkeleton } from './PaymentsScreenSkeleton';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { PageHeader, GlassCard } from '@/components/residents/redesign';
import {
  PaymentBalanceDueCard,
  PaymentMethodsSection,
  RecurringChargesCard,
  TransactionHistorySection,
  AutopaySection,
} from '@/components/residents/redesign/payments';
import type { RecurringChargeItem, AutopayLease } from '@/components/residents/redesign/payments';
import { selectLease, hasAutoPay, buildAutopayLease } from '@/features/payments/utils/leaseAutoPay';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ChargeBreakdown } from '@/features/payments/components/shared/ChargeBreakdown';


const GroupIcon = SVG_COMPONENTS.GROUP;
const HP = REDESIGN_SPACING.horizontalPadding;

interface LeaseDisplayData {
  leaseId: number;
  propertyName: string;
  unitNumber: string;
  balanceDue: number;
  accountCredit: number;
  balanceItems: any[] | undefined;
  autopayEnabled: boolean;
  autopayLease: AutopayLease;
  recurringCharges: RecurringChargeItem[];
  isConnection: boolean;
  paymentsBlocked: boolean;
  pendingPaymentsCount: number;
}

const useLeaseData = (leaseId: number, isConnection: boolean): { data: LeaseDisplayData | null; isFetching: boolean } => {
  const { balanceDue, balanceItems, accountCredit } = usePaymentBalance(leaseId);

  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId },
  );

  const { isFetching, isError, isSuccess, data: rawData } = leaseQuery;

  const data = useMemo<LeaseDisplayData | null>(() => {
    if (isFetching || isError || !isSuccess || !rawData) return null;

    const response = (rawData as any)?.response ?? rawData;
    const lease = selectLease(rawData);
    const property = lease?.property || {};
    const scheduledCharges = lease?.scheduledCharges || [];
    const users = lease?.users || [];
    const primaryUser = users[0] || {};

    const payments: any[] = response?.payments || [];
    const pendingPaymentsCount = payments.filter(
      (p: any) => !p.settlementStatus || p.settlementStatus === 'Pending Finalization'
    ).length;

    const autopayEnabled = hasAutoPay(lease);
    const propertyName = property?.marketingName || '';
    const autopayLease = buildAutopayLease({ leaseId, lease, primaryUser });

    return {
      leaseId,
      propertyName,
      unitNumber: lease?.unitNumberSpace || lease?.unitNumber || '',
      balanceDue,
      accountCredit,
      balanceItems,
      autopayEnabled,
      autopayLease,
      recurringCharges: scheduledCharges.map((c: any) => ({
        name: c.chargeCode || c.description || 'Charge',
        amount: c.amount ? Number(c.amount) : null,
        frequency: 'Monthly',
      })),
      isConnection,
      paymentsBlocked: !!lease?.paymentsBlocked,
      pendingPaymentsCount,
    };
  }, [leaseId, isFetching, isError, isSuccess, rawData, balanceDue, accountCredit, balanceItems, isConnection]);

  return { data, isFetching };
};

const PaymentsContent: React.FC = () => {
  const { colors } = useThemeColors();
  const styles = usePaymentsStyles();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();

  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const linkedAccounts = useAppSelector(linkedAcctsConnections);
  const isLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const [refreshing, setRefreshing] = useState(false);
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userId = authUser?.id;

  useEffect(() => {
    if (!linkedAccounts || linkedAccounts.length === 0) {
      dispatch(fetchLinkedAccounts());
    }
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchLinkedAccounts()).unwrap();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const allLeaseIds = [...ownLeaseIds, ...connectionLeaseIds];
  const hasLeases = allLeaseIds.length > 0;
  const primaryLeaseId = ownLeaseIds[0] || connectionLeaseIds[0];
  const isPrimaryConnection = connectionLeaseIds.includes(primaryLeaseId);

  const {
    rows: transactions,
  } = useLeaseHistory(primaryLeaseId, userId);

  const [selectedTransaction, setSelectedTransaction] = useState<HistoryRow | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);

  const handleTransactionPress = (transaction: HistoryRow) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleCloseTransactionDetails = () => {
    setShowTransactionDetails(false);
    setSelectedTransaction(null);
  };

  return (
    <>
      {isLoading && !hasLeases ? (
        <PaymentsScreenSkeleton />
      ) : !hasLeases ? (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />
          }
        >
          <PageHeader title="Payments" />
          <EmptyPaymentsState />
        </ScrollView>
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />
          }
        >
          <LeasePaymentsView
            leaseId={primaryLeaseId}
            isConnection={isPrimaryConnection}
            transactions={transactions}
            onTransactionPress={handleTransactionPress}
          />
        </ScrollView>
      )}
      <TransactionDetailsModal
        visible={showTransactionDetails}
        onClose={handleCloseTransactionDetails}
        transaction={selectedTransaction}
      />
    </>
  );
};

const EmptyPaymentsState: React.FC = React.memo(() => {
  const { colors } = useThemeColors();
  const styles = usePaymentsStyles();
  return (
    <View style={styles.emptyContainer}>
      <GlassCard style={styles.emptyCard}>
        <View style={styles.emptyIconContainer}>
          {GroupIcon && <GroupIcon width={48} height={48} color={colors.text.dimmer} />}
        </View>
        <Text style={styles.emptyTitle}>No recipients</Text>
        <Text style={styles.emptyDescription}>
          Once you receive and accept an invitation your recipients will be displayed here
        </Text>
      </GlassCard>
      <TransactionHistorySection transactions={[]} style={styles.emptyHistorySection} />
    </View>
  );
});

interface LeasePaymentsViewProps {
  leaseId: number;
  isConnection: boolean;
  transactions: HistoryRow[];
  onTransactionPress: (t: HistoryRow) => void;
}

const LeasePaymentsView: React.FC<LeasePaymentsViewProps> = React.memo(({
  leaseId,
  isConnection,
  transactions,
  onTransactionPress,
}) => {
  const { colors } = useThemeColors();
  const styles = usePaymentsStyles();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { data: leaseData, isFetching } = useLeaseData(leaseId, isConnection);
  const { methods: paymentMethods } = usePaymentMethodsList();
  const [showRecurringCharges, setShowRecurringCharges] = useState(false);
  const [autopayModalMode, setAutopayModalMode] = useState<'setup' | 'view' | null>(null);

  if (isFetching || !leaseData) {
    return (
      <>
        <PageHeader title="Payments" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={colors.text.muted} />
        </View>
      </>
    );
  }

  const subtitle = leaseData.unitNumber
    ? `${leaseData.propertyName} · Unit ${leaseData.unitNumber}`
    : leaseData.propertyName;

  const handleMakePayment = () => {
    navigation.navigate('MakePayment', { leaseId });
  };

  const handleManagePaymentMethods = () => {
    navigation.navigate('ManageWallet', {});
  };

  const handleAddPaymentMethod = () => {
    navigation.navigate('ManageWallet', { initialScreen: 'add' });
  };

  const handleViewAllTransactions = () => {
    navigation.navigate('TransactionHistory', { leaseId });
  };

  const handleAutopaySetup = () => {
    setAutopayModalMode('setup');
  };

  const handleAutopayEdit = () => {
    setAutopayModalMode('view');
  };

  const handleAutopayRemove = () => {
    setAutopayModalMode('view');
  };

  return (
    <>
      <PageHeader title="Payments" subtitle={subtitle} />

      <PaymentBalanceDueCard
        balanceDue={leaseData.balanceDue}
        accountCredit={leaseData.accountCredit}
        autopayEnabled={leaseData.autopayEnabled}
        recurringChargesCount={leaseData.recurringCharges.length}
        onMakePayment={handleMakePayment}
        onViewBreakdown={() => setShowRecurringCharges(!showRecurringCharges)}
        style={styles.sectionSpacing}
      />

      {leaseData.balanceItems && leaseData.balanceItems.length > 0 && (
        <ChargeBreakdown
          balanceItems={leaseData.balanceItems}
          balanceDue={leaseData.balanceDue}
          variant="desktop"
          style={styles.sectionSpacing}
        />
      )}

      {paymentMethods.length > 0 && (
        <PaymentMethodsSection
          methods={paymentMethods}
          onManage={handleManagePaymentMethods}
          onAddNew={handleAddPaymentMethod}
        />
      )}

      <AutopaySection
        leases={[leaseData.autopayLease]}
        onSetup={handleAutopaySetup}
        onEdit={handleAutopayEdit}
        onRemove={handleAutopayRemove}
      />

      {(showRecurringCharges || leaseData.recurringCharges.length > 0) && (
        <RecurringChargesCard charges={leaseData.recurringCharges} />
      )}

      <TransactionHistorySection
        transactions={transactions}
        maxItems={5}
        onViewAll={handleViewAllTransactions}
        onTransactionPress={onTransactionPress}
        style={styles.sectionSpacing}
      />

      {autopayModalMode !== null && (
        <MobileAutopayDetailsModal
          visible
          onClose={() => setAutopayModalMode(null)}
          leaseId={leaseId}
          onAutopayUpdated={() => {}}
          mode={autopayModalMode}
        />
      )}
    </>
  );
});

export const ResidentPaymentsScreen: React.FC = () => (
  <PaymentSummaryProvider>
    <PaymentsContent />
  </PaymentSummaryProvider>
);

const usePaymentsStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      scrollView: {
        flex: 1,
      },
      scrollContent: {
        paddingHorizontal: HP,
      },
      loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
      },
      sectionSpacing: {
        marginTop: 20,
      },
      emptyContainer: {
        marginTop: 20,
      },
      emptyCard: {
        alignItems: 'center',
        paddingVertical: 40,
      },
      emptyIconContainer: {
        marginBottom: 16,
      },
      emptyTitle: {
        ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
        color: colors.text.primary,
        marginBottom: 8,
        textAlign: 'center',
      },
      emptyDescription: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.muted,
        textAlign: 'center',
        lineHeight: 21,
      },
      emptyHistorySection: {
        marginTop: 24,
      },
    }),
  );
